// ZakupyByCountryChart.js — Legend like the screenshot (aligned + wording)
import React, { useEffect, useState } from "react";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "leaflet/dist/leaflet.css";

// ===== Styled =====
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

/* ----- Legend layout (aligned like screenshot) ----- */
const LegendWrap = styled.div`
  max-width: 800px;
  margin: 24px auto 0;
  padding: 20px;
`;

const LegendTitle = styled.h4`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 18px;
  color: #333;
`;

const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;   /* two columns */
  gap: 16px 48px;                   /* row gap / column gap */
  align-items: start;
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 22px auto; /* swatch | text */
  column-gap: 10px;
  align-items: center;
  min-height: 28px;
`;

const Swatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1px solid #999;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const LegendTexts = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const LegendLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
`;

const LegendDesc = styled.div`
  font-size: 12px;
  color: #555;
`;

// ===== Helpers =====
const formatNumber = (value) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// Palette to match screenshot feel
const COLOR_VERY_HIGH = "#2e7d32"; // dark green
const COLOR_HIGH      = "#81c784"; // light green
const COLOR_MEDIUM    = "#ffeb3b"; // yellow
const COLOR_LOW       = "#d32f2f"; // red
const COLOR_NONE      = "#bdbdbd"; // grey

// ===== Component =====
const ZakupyByCountryChart = ({ purchaseData = [] }) => {
  const [geoData, setGeoData] = useState(null);
  const [countryPurchaseData, setCountryPurchaseData] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("00");
  const [availableYears, setAvailableYears] = useState([]);
  const [error, setError] = useState(null);

  // Load Europe GeoJSON
  useEffect(() => {
    const fetchGeoData = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson"
        );
        const data = await response.json();
        setGeoData(data);
      } catch {
        setError("Błąd ładowania danych mapy Europy.");
      }
    };
    fetchGeoData();
  }, []);

  // Years list & default selection
  useEffect(() => {
    if (purchaseData.length === 0) return;
    const years = [...new Set(purchaseData.map((row) => row.Rok))].sort();
    setAvailableYears(years);
    if (!selectedYear && years.length) {
      setSelectedYear(years[years.length - 1]); // latest
    }
  }, [purchaseData, selectedYear]);

  // Aggregate purchases by country for the selected period (K_42 + K_43)
  useEffect(() => {
    if (!selectedYear) return;

    const filtered = purchaseData.filter(
      (row) =>
        row.Rok === selectedYear &&
        (selectedMonth === "00" || row.Miesiac === parseInt(selectedMonth))
    );

    const grouped = filtered.reduce((acc, row) => {
      const amount = parseFloat(row.K_42) || 0;
      const vat = parseFloat(row.K_43) || 0;
      const total = amount + vat;
      const country = row.KodKrajuNadaniaTIN?.trim() || "Unknown";
      acc[country] = (acc[country] || 0) + total;
      return acc;
    }, {});

    const total = Object.values(grouped).reduce((sum, v) => sum + v, 0);

    const formatted = Object.keys(grouped)
      .filter((c) => c !== "OTHER")
      .map((c) => ({
        country: c,
        value: grouped[c],
        percentage: total ? ((grouped[c] / total) * 100).toFixed(2) : "0",
      }));

    setCountryPurchaseData(formatted);
  }, [purchaseData, selectedYear, selectedMonth]);

  // Choropleth color scale (GREEN good → RED bad)
  const styleFeature = (feature) => {
    const code = (feature.properties.ISO2 || "").trim();
    const data = countryPurchaseData.find((c) => c.country === code);
    const pct = data ? parseFloat(data.percentage) : 0;

    const getColor = (p) => {
      if (p >= 50) return COLOR_VERY_HIGH; // ≥ 50%
      if (p >= 25) return COLOR_HIGH;      // 25–49%
      if (p >= 10) return COLOR_MEDIUM;    // 10–24%
      if (p > 0)  return COLOR_LOW;        // 1–9%
      return COLOR_NONE;                   // 0%
    };

    return {
      fillColor: getColor(pct),
      weight: 0.7,
      opacity: 0.4,
      color: "#444",
      fillOpacity: 0.3,
    };
  };

  // Popups
  const onEachFeature = (feature, layer) => {
    const code = (feature.properties.ISO2 || "").trim();
    const data = countryPurchaseData.find((d) => d.country === code);
    const value = data ? `${formatNumber(data.value)} zł` : "Brak danych";
    const pct = data ? `${data.percentage}%` : "0%";

    layer.bindPopup(
      `<strong>Kraj:</strong> ${feature.properties.NAME}<br/>
       <strong>Zakupy:</strong> ${value}<br/>
       <strong>Udział:</strong> ${pct}`
    );
  };

  const months = [
    ["00", "Cały rok"],
    ["01", "Styczeń"],
    ["02", "Luty"],
    ["03", "Marzec"],
    ["04", "Kwiecień"],
    ["05", "Maj"],
    ["06", "Czerwiec"],
    ["07", "Lipiec"],
    ["08", "Sierpień"],
    ["09", "Wrzesień"],
    ["10", "Październik"],
    ["11", "Listopad"],
    ["12", "Grudzień"],
  ];

  return (
    <Wrapper>
      <ChartTitle>
        Udział Zakupów wg Krajów
        <InfoIconWrapper
          data-tooltip-id="purchase-map-tooltip"
          data-tooltip-html="Mapa udziału zakupów wg kraju. Kliknij kraj, aby zobaczyć szczegóły."
        >
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip id="purchase-map-tooltip" place="left" />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {error ? (
        <p style={{ marginTop: "20px", color: "#c62828", textAlign: "center" }}>{error}</p>
      ) : !geoData || countryPurchaseData.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888", textAlign: "center" }}>
          Brak danych do wyświetlenia.
        </p>
      ) : (
        <>
          <MapContainer
            style={{ height: "500px", width: "100%" }}
            center={[51.2, 15.2]}
            zoom={4.2}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <GeoJSON data={geoData} style={styleFeature} onEachFeature={onEachFeature} />
          </MapContainer>

          {/* Aligned Legend like screenshot */}
          <LegendWrap>
            <LegendTitle>Legenda mapy udziałów przychodów</LegendTitle>
            <LegendGrid>
              {[
                { color: COLOR_VERY_HIGH, label: "≥ 50%",     desc: "bardzo wysoki udział (dobry)" },
                { color: COLOR_HIGH,      label: "25% - 49%", desc: "wysoki udział (dobry)" },
                { color: COLOR_MEDIUM,    label: "10% - 24%", desc: "średni udział (neutralny)" },
                { color: COLOR_LOW,       label: "1% - 9%",   desc: "niski udział (zły)" },
                { color: COLOR_NONE,      label: "0%",        desc: "brak udziału" },
              ].map((item, i) => (
                <LegendItem key={i}>
                  <Swatch style={{ backgroundColor: item.color }} />
                  <LegendTexts>
                    <LegendLabel>{item.label}</LegendLabel>
                    <LegendDesc>{item.desc}</LegendDesc>
                  </LegendTexts>
                </LegendItem>
              ))}
            </LegendGrid>
          </LegendWrap>
        </>
      )}
    </Wrapper>
  );
};

export default ZakupyByCountryChart;
